import { call, put, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  generateCampaignRequest,
  generateCampaignSuccess,
  generateCampaignFailure,
  type GenerateCampaignPayload,
} from '../slices/campaignGenerationSlice';
import { ENDPOINTS } from '../../api/endPoints';

interface GenerateCampaignAPIResponse {
  message: string;
  data: {
    idea_id: number;
    idea_type: string;
    idea_headline: string;
    color_theme: string[];
    created_at: string;
  };
}

function* generateCampaignSaga(action: PayloadAction<GenerateCampaignPayload>) {
  try {
    const { restaurantId, image, ...formFields } = action.payload;
    
    // Create FormData for multipart/form-data
    const formData = new FormData();
    
    // Append form fields
    formData.append('campaign_occasion', formFields.campaign_occasion);
    formData.append('campaign_date', formFields.campaign_date);
    formData.append('target_audience', JSON.stringify(formFields.target_audience));
    formData.append('campaign_type', formFields.campaign_type);
    formData.append('offer_details', formFields.offer_details);
    formData.append('color_theme', formFields.color_theme);
    formData.append('idea_type', formFields.idea_type);
    
    // Append image if provided
    if (image) {
      formData.append('image', image);
    }

    const url = `${ENDPOINTS.generateCampaign}/${restaurantId}`;
    
    const response: Response = yield call(fetch, url, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header - browser will set it with boundary for FormData
    });

    if (!response.ok) {
      const errorData: { message?: string } = yield response.json();
      throw new Error(errorData.message || 'Failed to generate campaign');
    }

    const apiResponse: GenerateCampaignAPIResponse = yield response.json();

    yield put(generateCampaignSuccess({
      ideaId: apiResponse.data.idea_id,
      ideaType: apiResponse.data.idea_type,
      message: apiResponse.message || 'Campaign generation started successfully',
    }));
  } catch (error: any) {
    yield put(generateCampaignFailure(error.message || 'An error occurred'));
  }
}

export default function* campaignGenerationSagaWatcher() {
  yield takeLatest(generateCampaignRequest.type, generateCampaignSaga);
}
